import { useState, useEffect } from "react";
import PublicMenuPage from "../pages/PublicMenuPage";
import { API_URL } from "@/utils/apiConfig";
import { useTranslation } from "react-i18next";
import BrandedMenuLoader from "@/components/public/BrandedMenuLoader";

// Module-level: logs as soon as the JS bundle loads on any custom domain page
console.log('[DEPLOY_VERSION_DEEP_LINKS_V3_ACTIVE]', new Date().toISOString());

const API = API_URL || "";
const KAISO_ROOT = "kaisosystem.com";
const RESERVED_SUBDOMAINS = new Set(["www", "app", "api", "admin", "mail", "staging", "dev", "preview"]);

/**
 * Returns the restaurant slug if the hostname is a *.kaisosystem.com subdomain,
 * null otherwise.
 * e.g. "kaiso-sushi.kaisosystem.com" → "kaiso-sushi"
 *      "kaisosystem.com"             → null
 *      "kaisosushi.es"               → null
 */
export function getKaisoSubdomain(hostname = window.location.hostname) {
  if (!hostname.endsWith(`.${KAISO_ROOT}`)) return null;
  const sub = hostname.slice(0, hostname.length - KAISO_ROOT.length - 1);
  if (!sub || RESERVED_SUBDOMAINS.has(sub) || sub.includes(".")) return null;
  return sub;
}

/**
 * Returns true when the current hostname should render a restaurant menu:
 * - *.kaisosystem.com subdomains (e.g. kaiso-sushi.kaisosystem.com)
 * - Any other custom domain (e.g. kaisosushi.es)
 */
export function isCustomDomain(hostname = window.location.hostname) {
  // *.kaisosystem.com restaurant subdomain → always show menu
  if (getKaisoSubdomain(hostname)) return true;
  // Known platform hosts → landing page / dashboard
  const KNOWN = [KAISO_ROOT, "localhost", "preview.emergentagent.com", "emergent.host", "onrender.com", "127.0.0.1"];
  if (KNOWN.some(h => hostname === h || hostname.endsWith(`.${h}`))) return false;
  // All other hostnames are custom restaurant domains
  return true;
}

function parseDeepLinkSlug() {
  const parts = window.location.pathname.split('/');
  // /product/ is the canonical route
  const productIdx = parts.indexOf('product');
  if (productIdx > 0 && parts[productIdx + 1]) return parts[productIdx + 1];
  // /oferta/ kept for backward compat only
  const ofertaIdx = parts.indexOf('oferta');
  if (ofertaIdx > 0 && parts[ofertaIdx + 1]) return parts[ofertaIdx + 1];
  return null;
}

export default function CustomDomainMenu() {
  const { t } = useTranslation();
  const [restaurantId, setRestaurantId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const domain = window.location.hostname;
  const kaisoSlug = getKaisoSubdomain(domain);

  useEffect(() => {
    // Custom domains: pass domain directly as restaurantId.
    // The backend's resolve_public_restaurant() already handles id / slug / custom_domain
    // lookup in a single step, so the extra resolve-domain round-trip is unnecessary
    // and was the point of failure when custom_domain was temporarily missing from the DB.
    if (!kaisoSlug) {
      setRestaurantId(domain);
      setLoading(false);
      return;
    }

    // Kaiso subdomains (*.kaisosystem.com): resolve slug → canonical restaurantId first.
    const tryResolve = async (attempt = 1) => {
      const url = `${API}/api/v1/public/resolve/${kaisoSlug}`;
      let timeoutId;
      try {
        const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
        if (controller) {
          timeoutId = setTimeout(() => controller.abort(), 8000);
        }
        const r = await fetch(url, controller ? { signal: controller.signal } : undefined);
        if (r.ok) {
          const body = await r.json();
          const data = body?.data ?? body;
          setRestaurantId(data.restaurant_id);
          setLoading(false);
          return;
        }
      } catch {
        if (attempt === 1) {
          return tryResolve(2);
        }
      } finally {
        if (timeoutId) clearTimeout(timeoutId);
      }
      setError(t("publicMenu.errors.restaurantNotFound"));
      setLoading(false);
    };

    tryResolve();
  }, [t, domain, kaisoSlug]);

  if (loading) {
    return <BrandedMenuLoader />;
  }

  if (error || !restaurantId) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#0a0a0a' }} data-testid="custom-domain-error">
        <div className="text-center">
          <p className="text-xl mb-2 text-white/80">{error || t("publicMenu.errors.restaurantNotFound")}</p>
          <p className="text-white/30 text-sm">{t("publicMenu.errors.checkDomainConfig")}</p>
        </div>
      </div>
    );
  }

  const _deepLinkSlug = parseDeepLinkSlug();
  const _isLandingMode = window.location.pathname.includes('/product/');
  console.log('[DEEP_LINK_PATHNAME]', window.location.pathname);
  console.log('[DEEP_LINK_SLUG_EXTRACTED]', _deepLinkSlug || '(none)');
  console.log('[DEEP_LINK_RESTAURANT_SLUG]', restaurantId);
  console.log('[DEEP_LINK_LANDING_MODE]', _isLandingMode);
  return <PublicMenuPage overrideRestaurantId={restaurantId} overrideDeepLinkSlug={_deepLinkSlug} overrideLandingMode={_isLandingMode} />;
}
